<script setup>
import { computed, ref, watch, nextTick, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import InternalCommunicationAPI from 'dashboard/api/internalCommunication';
import MessageContent from 'dashboard/routes/dashboard/internalCommunication/MessageContent.vue';
import InternalAttachmentList from 'dashboard/routes/dashboard/internalCommunication/InternalAttachmentList.vue';
import CallControl from './CallControl.vue';

const props = defineProps({
  roomId: { type: [Number, String], required: true },
});
defineEmits(['close']);
const { t, locale } = useI18n();
const store = useStore();
const draft = defineModel('draft', { type: String, default: '' });
const loading = ref(false);
const sending = ref(false);
const error = ref(false);
const feed = ref(null);
const heading = ref(null);
const hasMore = ref(false);
const room = computed(() =>
  store.getters['internalCommunication/getRoom'](props.roomId)
);
const messages = computed(() =>
  store.getters['internalCommunication/getMessages'](props.roomId)
);
let revision = 0;
const scroll = () =>
  nextTick(() => {
    if (feed.value) feed.value.scrollTop = feed.value.scrollHeight;
  });
const load = async (older = false) => {
  revision += 1;
  const expected = revision;
  loading.value = true;
  error.value = false;
  try {
    const [roomResult, result] = await Promise.all([
      InternalCommunicationAPI.getRoom(props.roomId),
      InternalCommunicationAPI.getMessages(
        props.roomId,
        older ? messages.value[0]?.sequence : undefined
      ),
    ]);
    if (expected !== revision) return;
    store.commit('internalCommunication/UPSERT_ROOM', roomResult.data);
    store.commit('internalCommunication/SET_MESSAGES', {
      roomId: props.roomId,
      messages: result.data.messages,
      prepend: older,
    });
    hasMore.value = result.data.meta.has_more;
    if (!older) scroll();
  } catch {
    if (expected === revision) error.value = true;
  } finally {
    if (expected === revision) loading.value = false;
  }
};
const send = async () => {
  if (!draft.value.trim() || sending.value || !room.value?.can_post) return;
  sending.value = true;
  error.value = false;
  try {
    await store.dispatch('internalCommunication/sendMessage', {
      roomId: props.roomId,
      message: { content: draft.value.trim(), client_id: crypto.randomUUID() },
    });
    draft.value = '';
    scroll();
  } catch {
    error.value = true;
  } finally {
    sending.value = false;
  }
};
const keydown = event => {
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    send();
  }
};
const time = value =>
  new Date(value).toLocaleTimeString(locale.value.replace('_', '-'), {
    hour: '2-digit',
    minute: '2-digit',
  });
watch(
  () => props.roomId,
  (_, previousRoomId) => {
    if (previousRoomId !== undefined) draft.value = '';
    load();
    nextTick(() => heading.value?.focus());
  },
  { immediate: true }
);
watch(
  () => messages.value.length,
  () => {
    const el = feed.value;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 100) scroll();
  }
);
onBeforeUnmount(() => {
  revision += 1;
});
</script>

<template>
  <section
    class="flex min-h-0 flex-1 flex-col"
    :aria-label="t('INTERNAL_CALL.CHAT')"
    @keydown.esc.stop="$emit('close')"
  >
    <header
      class="flex shrink-0 items-center justify-between border-b border-n-weak px-4 py-3"
    >
      <div>
        <h3
          ref="heading"
          tabindex="-1"
          class="m-0 text-base font-semibold text-n-slate-12 outline-none"
        >
          {{ t('INTERNAL_CALL.CHAT') }}
        </h3>
        <p class="m-0 mt-0.5 text-xs text-n-slate-11">
          {{ t('INTERNAL_CALL.CHAT_ROOM_HELP') }}
        </p>
      </div>
      <CallControl
        icon="i-lucide-x"
        :label="t('INTERNAL_CALL.CLOSE_CHAT')"
        @click="$emit('close')"
      />
    </header>
    <div
      v-if="error"
      role="alert"
      class="m-3 rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
    >
      {{ t('INTERNAL_CALL.CHAT_ERROR') }}
      <button type="button" class="ml-1 underline" @click="load()">
        {{ t('INTERNAL_CALL.RETRY') }}
      </button>
    </div>
    <div
      ref="feed"
      class="min-h-0 flex-1 space-y-5 overflow-y-auto p-4"
      role="log"
      :aria-label="t('INTERNAL_CALL.CHAT')"
      :aria-busy="loading"
    >
      <button
        v-if="hasMore"
        type="button"
        :disabled="loading"
        class="w-full rounded-lg p-2 text-sm text-n-blue-11 hover:bg-n-alpha-2"
        @click="load(true)"
      >
        {{ t('INTERNAL_CALL.OLDER_MESSAGES') }}
      </button>
      <p v-if="loading && !messages.length" class="text-sm text-n-slate-11">
        {{ t('INTERNAL_CALL.CHAT_LOADING') }}
      </p>
      <p
        v-else-if="!messages.length && !error"
        class="py-8 text-center text-sm leading-6 text-n-slate-11"
      >
        {{ t('INTERNAL_CALL.CHAT_EMPTY') }}
      </p>
      <article
        v-for="message in messages"
        :key="message.id"
        class="flex gap-2.5"
      >
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-blue-3 text-xs font-semibold text-n-blue-11"
          aria-hidden="true"
          >{{
            (message.author?.name || message.author_name || '?').slice(0, 1)
          }}</span
        >
        <div class="min-w-0 flex-1">
          <div class="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span class="text-xs font-semibold text-n-slate-12">{{
              message.author?.name ||
              message.author_name ||
              t('INTERNAL_CALL.YOU')
            }}</span
            ><time
              class="text-[11px] text-n-slate-11"
              :datetime="message.created_at"
              >{{ time(message.created_at) }}</time
            >
          </div>
          <p v-if="message.deleted" class="m-0 text-xs italic text-n-slate-11">
            {{ t('INTERNAL_COMMUNICATION.MESSAGES.DELETED') }}
          </p>
          <div v-else class="rounded-lg bg-n-alpha-1 p-2.5 text-n-slate-12">
            <MessageContent
              :content="message.content || ''"
            /><InternalAttachmentList
              :attachments="message.attachments || []"
            />
          </div>
        </div>
      </article>
    </div>
    <form
      class="m-3 mt-0 shrink-0 rounded-xl border border-n-strong bg-n-field p-2 focus-within:border-n-blue-9"
      @submit.prevent="send"
    >
      <textarea
        v-model="draft"
        rows="2"
        :disabled="!room?.can_post || sending"
        :aria-label="t('INTERNAL_CALL.MESSAGE_PLACEHOLDER')"
        :aria-describedby="
          !room?.can_post ? 'internal-call-read-only' : undefined
        "
        :placeholder="
          t(
            room?.can_post
              ? 'INTERNAL_CALL.MESSAGE_PLACEHOLDER'
              : 'INTERNAL_CALL.CHAT_READ_ONLY'
          )
        "
        class="block w-full resize-none border-0 bg-transparent p-1 text-sm text-n-slate-12 outline-none placeholder:enabled:text-n-slate-11 focus:border-0 focus:ring-0 disabled:opacity-60"
        @keydown="keydown"
      />
      <p
        v-if="!room?.can_post"
        id="internal-call-read-only"
        role="status"
        class="m-1 text-xs leading-5 text-n-slate-11"
      >
        {{ t('INTERNAL_CALL.CHAT_READ_ONLY') }}
      </p>
      <div class="flex justify-end">
        <CallControl
          icon="i-lucide-send"
          :label="t('INTERNAL_CALL.SEND_MESSAGE')"
          :disabled="!draft.trim() || sending || !room?.can_post"
          active
          @click="send"
        />
      </div>
    </form>
  </section>
</template>
